import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { EncryptStorage } from 'encrypt-storage';
import { cachePrefix, storageKey } from '../constants/storageConstants';
import * as Sentry from '@sentry/browser';
import * as jwt from 'jsonwebtoken';
import { useAuthStore } from '@stores/authStore';
/**
 * Extracts the stable oid from a JWT idToken.
 * Returns null if decoding fails or oid is missing to prevent volatile storage keys.
 */ export var getStableIdFromToken = function getStableIdFromToken(idToken) {
    try {
        var decoded = jwt.decode(idToken);
        if (decoded === null || decoded === void 0 ? void 0 : decoded.oid) return decoded.oid;
    } catch (unused) {
    // Decoding failed, return null to indicate failure
    }
    return null;
};
/**
 * Generates a hashed encryption key from a user identifier.
 */ var hashIdentifier = function hashIdentifier(identifier) {
    return _async_to_generator(function() {
        var _String, encoder, data, hashBuffer, hashArray;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    encoder = new TextEncoder();
                    data = encoder.encode(identifier);
                    return [
                        4,
                        crypto.subtle.digest('SHA-256', data)
                    ];
                case 1:
                    hashBuffer = _state.sent();
                    hashArray = Array.from(new Uint8Array(hashBuffer));
                    return [
                        2,
                        btoa((_String = String).fromCharCode.apply(_String, _to_consumable_array(hashArray))).replace(/=+$/, '')
                    ];
            }
        });
    })();
};
/**
 * Retrieves cached user data from encrypted localStorage.
 * @param oid - Stable user identifier (Azure Object ID)
 */ export var getLocalStorageData = function getLocalStorageData(oid) {
    return _async_to_generator(function() {
        var key, encryptStorage, _e_message, isMalformedError;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    return [
                        4,
                        hashIdentifier(oid)
                    ];
                case 1:
                    key = _state.sent();
                    encryptStorage = new EncryptStorage(key, {
                        prefix: cachePrefix
                    });
                    try {
                        return [
                            2,
                            encryptStorage.getItem(storageKey)
                        ];
                    } catch (e) {
                        ;
                        isMalformedError = e === null || e === void 0 ? void 0 : (_e_message = e.message) === null || _e_message === void 0 ? void 0 : _e_message.includes('Malformed UTF-8');
                        if (isMalformedError) {
                            // Data is corrupted or encrypted with different key - clear and continue
                            try {
                                encryptStorage.removeItem(storageKey);
                            } catch (unused) {
                            // Ignore cleanup errors
                            }
                        } else {
                            // Unexpected error - report to Sentry
                            Sentry.captureException(e, {
                                tags: {
                                    storageKey: storageKey
                                }
                            });
                        }
                        return [
                            2,
                            null
                        ];
                    }
                    return [
                        2
                    ];
            }
        });
    })();
};
/**
 * Saves user data to encrypted localStorage.
 * @param oid - Stable user identifier (Azure Object ID)
 * @param data - Data to save
 */ export var setLocalStorageData = function setLocalStorageData(oid, data) {
    return _async_to_generator(function() {
        var key, encryptStorage, e;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    _state.trys.push([
                        0,
                        2,
                        ,
                        3
                    ]);
                    return [
                        4,
                        hashIdentifier(oid)
                    ];
                case 1:
                    key = _state.sent();
                    encryptStorage = new EncryptStorage(key, {
                        prefix: cachePrefix
                    });
                    encryptStorage.setItem(storageKey, data);
                    return [
                        2,
                        true
                    ];
                case 2:
                    e = _state.sent();
                    Sentry.captureException(e, {
                        tags: {
                            storageKey: storageKey,
                            context: 'setLocalStorageData'
                        }
                    });
                    return [
                        2,
                        false
                    ];
                case 3:
                    return [
                        2
                    ];
            }
        });
    })();
};
/**
 * Removes cached user data from encrypted localStorage.
 * @param oid - Stable user identifier (Azure Object ID)
 */ export var removeLocalStorageData = function removeLocalStorageData(oid) {
    return _async_to_generator(function() {
        var key, encryptStorage, e;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    if (!oid) return [
                        2
                    ];
                    _state.label = 1;
                case 1:
                    _state.trys.push([
                        1,
                        3,
                        ,
                        4
                    ]);
                    return [
                        4,
                        hashIdentifier(oid)
                    ];
                case 2:
                    key = _state.sent();
                    encryptStorage = new EncryptStorage(key, {
                        prefix: cachePrefix
                    });
                    encryptStorage.removeItem(storageKey);
                    return [
                        2,
                        true
                    ];
                case 3:
                    e = _state.sent();
                    Sentry.captureException(e, {
                        tags: {
                            storageKey: storageKey,
                            context: 'removeLocalStorageData'
                        }
                    });
                    return [
                        2,
                        false
                    ];
                case 4:
                    return [
                        2
                    ];
            }
        });
    })();
};
/**
 * Updates a single userSettings property in the encrypted localStorage cache.
 * @param settingKey - The userSettings property key to update
 * @param value - The new value
 */ export var updateCachedUserSetting = function updateCachedUserSetting(settingKey, value) {
    return _async_to_generator(function() {
        var oid, data, error;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    _state.trys.push([
                        0,
                        3,
                        ,
                        4
                    ]);
                    oid = useAuthStore.getState().oidFromIdToken;
                    if (!oid) return [
                        2,
                        false
                    ];
                    return [
                        4,
                        getLocalStorageData(oid)
                    ];
                case 1:
                    data = _state.sent();
                    if (!data) return [
                        2,
                        false
                    ];
                    data.userData = data.userData || {};
                    data.userData.userSettings = data.userData.userSettings || {};
                    data.userData.userSettings[settingKey] = value;
                    return [
                        4,
                        setLocalStorageData(oid, data)
                    ];
                case 2:
                    return [
                        2,
                        _state.sent()
                    ];
                case 3:
                    error = _state.sent();
                    // Log error to Sentry but don't revert state
                    Sentry.captureException(error, {
                        tags: {
                            settingKey: settingKey,
                            context: 'updateCachedUserSetting'
                        }
                    });
                    return [
                        2,
                        false
                    ];
                case 4:
                    return [
                        2
                    ];
            }
        });
    })();
};
